import { useState } from "react";
import type { OrderWithTimeline } from "../page";
import { STATUS_CONFIG } from "../page";

interface Props {
  order: OrderWithTimeline;
}

export default function OrderHeader({ order }: Props) {
  const [copied, setCopied] = useState(false);
  const cfg = STATUS_CONFIG[order.status];

  const handleCopy = () => {
    navigator.clipboard.writeText(order.orderRef);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const progressSteps = ["pending", "confirmed", "shipped", "delivered"];
  const currentStep = cfg?.step ?? 0;
  const isCancelled = order.status === "cancelled";
  const progressPercent = isCancelled ? 0 : Math.round((currentStep / (progressSteps.length - 1)) * 100);

  return (
    <div className="bg-[#141414] border border-white/5 rounded-2xl overflow-hidden">
      {/* Top accent bar */}
      <div className={`h-1 w-full ${
        isCancelled
          ? "bg-gradient-to-r from-red-500/0 via-red-500/70 to-red-500/0"
          : order.status === "delivered"
          ? "bg-gradient-to-r from-teal-400/0 via-teal-400/80 to-teal-400/0"
          : "bg-gradient-to-r from-amber-400/0 via-amber-400/80 to-amber-400/0"
      }`}></div>

      <div className="p-6">
        {/* Row: ref + status badge */}
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <p className="text-gray-500 text-xs mb-1" style={{ fontFamily: "'Cairo', sans-serif" }}>رقم الطلب</p>
            <div className="flex items-center gap-2">
              <h1 className="text-white font-black text-2xl tracking-wide" style={{ fontFamily: "'Cairo', sans-serif" }}>
                #{order.orderRef}
              </h1>
              <button
                onClick={handleCopy}
                title="نسخ رقم الطلب"
                className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 text-gray-500 hover:text-amber-400 transition-all cursor-pointer"
              >
                <i className={`text-sm ${copied ? "ri-check-line text-green-400" : "ri-file-copy-line"}`}></i>
              </button>
            </div>
            <p className="text-gray-600 text-xs mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
              <i className="ri-calendar-line ml-1"></i>
              {new Date(order.date).toLocaleDateString("ar-SA", {
                year: "numeric", month: "long", day: "numeric",
                hour: "2-digit", minute: "2-digit",
              })}
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {cfg && (
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full border ${cfg.bg} ${cfg.border}`}>
                <i className={`${cfg.icon} ${cfg.color} text-base`}></i>
                <span className={`${cfg.color} text-sm font-bold whitespace-nowrap`} style={{ fontFamily: "'Cairo', sans-serif" }}>
                  {cfg.label}
                </span>
              </div>
            )}
            {/* Bank transfer badge */}
            {(order.paymentLabel === "التحويل البنكي" || order.paymentLabel?.includes("بنك") || order.paymentLabel?.includes("تحويل")) && (
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${
                order.status === "pending"
                  ? "bg-orange-400/10 border-orange-400/30 text-orange-400"
                  : "bg-amber-400/10 border-amber-400/30 text-amber-400"
              }`}>
                <i className="ri-bank-line text-sm"></i>
                <span className="text-xs font-bold whitespace-nowrap" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  {order.status === "pending" ? "تحويل — بانتظار التأكيد" : "تحويل بنكي"}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Progress bar (only for non-cancelled) */}
        {!isCancelled && (
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-600 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>تقدم الطلب</span>
              <span className="text-amber-400 text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {progressPercent}%
              </span>
            </div>
            <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${progressPercent}%`,
                  background: order.status === "delivered"
                    ? "linear-gradient(90deg, #2dd4bf, #34d399)"
                    : "linear-gradient(90deg, #f59e0b, #fbbf24)",
                }}
              ></div>
            </div>

            {/* Step dots */}
            <div className="flex justify-between mt-3">
              {progressSteps.map((s, i) => {
                const done = currentStep >= i;
                const active = currentStep === i;
                const labels: Record<string, string> = {
                  pending: "استلام",
                  confirmed: "تأكيد",
                  shipped: "شحن",
                  delivered: "توصيل",
                };
                return (
                  <div key={s} className="flex flex-col items-center gap-1">
                    <div className={`w-2.5 h-2.5 rounded-full transition-all ${
                      done
                        ? active
                          ? "bg-amber-400 ring-2 ring-amber-400/30"
                          : "bg-green-400"
                        : "bg-white/10"
                    }`}></div>
                    <span className={`text-[10px] ${done ? (active ? "text-amber-400" : "text-green-400") : "text-gray-700"}`}
                      style={{ fontFamily: "'Cairo', sans-serif" }}>
                      {labels[s]}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Cancelled notice */}
        {isCancelled && (
          <div className="flex items-center gap-3 bg-red-400/5 border border-red-400/15 rounded-xl px-4 py-3">
            <i className="ri-close-circle-line text-red-400 text-lg"></i>
            <p className="text-red-400 text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
              تم إلغاء هذا الطلب. للاستفسار تواصل معنا عبر واتساب.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
